import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { TRACKING_MODES } from '../../../constants/trackingModes';

/**
 * Determines the tracking mode from action data
 */
const getActionTrackingMode = (action) => {
  // Try to get from parent goal's measurement type
  const trackingMode = action?.developmentGoal?.measurementType?.trackingMode;
  if (trackingMode) {
    return trackingMode;
  }

  // Infer from action data
  if (action?.currentRagStatus?.id) {
    return TRACKING_MODES.RAG;
  }
  // Check if currentProgress is defined (including 0) to detect progress mode
  if (typeof action?.currentProgress === 'number' && !action?.completedAt) {
    return TRACKING_MODES.PROGRESS;
  }
  return TRACKING_MODES.CHECKLIST;
};

/**
 * ActionTrackingStatus - Displays IDP action status based on tracking mode
 *
 * @param {Object} action - The IDP action object containing status data
 * @param {boolean} showDot - Whether to show the bullet separator (default: true)
 */
const ActionTrackingStatus = ({ action, showDot = true }) => {
  const trackingMode = getActionTrackingMode(action);

  const renderDot = () => showDot && (
    <Typography variant="caption" sx={{ color: '#9CA3AF' }}>•</Typography>
  );

  // RAG Mode - colored dot with status name
  if (trackingMode === TRACKING_MODES.RAG && action?.currentRagStatus) {
    return (
      <>
        {renderDot()}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: action.currentRagStatus.color || '#9ca3af',
          }} />
          <Typography variant="caption" sx={{ color: '#6B7280' }}>
            {action.currentRagStatus.name}
          </Typography>
        </Box>
      </>
    );
  }

  // Progress Mode - percentage chip
  if (trackingMode === TRACKING_MODES.PROGRESS) {
    return (
      <>
        {renderDot()}
        <Chip
          label={`${action?.currentProgress || 0}%`}
          size="small"
          sx={{
            height: 18,
            fontSize: '10px',
            backgroundColor: '#eff6ff',
            color: '#2563eb',
            fontWeight: 500,
          }}
        />
      </>
    );
  }

  // Default: Checklist Mode - Completed/Pending
  return (
    <>
      {renderDot()}
      <Typography
        variant="caption"
        sx={{
          color: action?.completedAt ? '#16A34A' : '#6B7280',
          fontWeight: action?.completedAt ? 500 : 400,
        }}
      >
        {action?.completedAt ? 'Completed' : 'Pending'}
      </Typography>
    </>
  );
};

export default ActionTrackingStatus;
